@if (isLoading) {
  <div class="spinner-overlay">
    <mat-spinner></mat-spinner>
  </div>
}

<h1 class="title">@if (isReadonly) {
  Upscaling Fluid Flow Model
} @else {
  Upscaling Fluid Flow Model - Phases definition
}</h1>

@if (!isLoading && rockFile) {
<mat-card class="definition-card">
  <div class="definition-layout">
    <div class="left-panel">
      <div class="file-info-card">
        <div class="info-row">
          <span class="info-label">Rock File:</span>
          <span class="info-value">{{ rockFileName | removeExtension }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Size:</span>
          <span class="info-value">{{ getFileSizeDisplay() }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Rock Model:</span>
          <a class="info-value link" [href]="getRockModelUrl()" target="_blank">{{ rockModelName }}</a>
        </div>
      </div>

      <div class="image-preview-card">
        <div class="image-header">Upscaling Segmented Rock Image</div>
        <div class="image-container">
          @if (thumbnailUrl) {
            <img [src]="thumbnailUrl" alt="Rock image thumbnail" class="rock-thumbnail" />
          } @else {
            <div class="placeholder-image">
              <mat-icon>image</mat-icon>
              <p>No preview available</p>
            </div>
          }
        </div>
      </div>
    </div>

    <div class="right-panel">
      <div class="simulation-config-section">
        <div class="info-row">
          <span class="info-label">Simulation name:</span>
          <a class="info-value link" [href]="getSimulationResultsUrl()" target="_blank">{{ simulationName }}</a>
        </div>

        <div class="info-row direction-row">
          <span class="info-label">Direction</span>
          <mat-radio-group [(ngModel)]="selectedDirection" class="direction-radio-group" [disabled]="isReadonly">
            @for (dir of directions; track dir.direction) {
              <mat-radio-button [value]="dir.direction" class="direction-radio" [disabled]="isReadonly || isDirectionDisabled(dir)">
                {{ getDirectionLabel(dir) }}
              </mat-radio-button>
            }
          </mat-radio-group>
        </div>

        <div class="info-row">
          <span class="info-label">Fluid Flow Name</span>
          <form [formGroup]="fluidFlowNameForm" class="name-form">
            <mat-form-field appearance="outline" class="name-input">
              <input
                matInput
                formControlName="name"
                placeholder="e.g., Ups_Flow_01"
                maxlength="256"
                required
                [readonly]="isReadonly"
              />
              @if (!isReadonly && fluidFlowNameForm.get('name')?.hasError('required')) {
                <mat-error>Name is required</mat-error>
              }
              @if (!isReadonly && fluidFlowNameForm.get('name')?.hasError('pattern')) {
                <mat-error>Only letters, numbers, hyphens and underscores allowed</mat-error>
              }
            </mat-form-field>
          </form>
        </div>
      </div>

      <div class="table-container-section">
        <h2 class="table-title">Labels properties</h2>
        <div class="table-wrapper">
          <table class="phase-table mat-elevation-z2">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Label</th>
                <th>Fraction</th>
                <th>Porosity<br/>Fraction</th>
                <th>Permeability<br/>(mD)</th>
                <th>Subsample<br/>Capillary<br/>Pressure</th>
                <th>Subsample<br/>Permeability<br/>(mD)</th>
              </tr>
            </thead>
            <tbody>
              @for (phase of phases; track phase.label) {
                <tr>
                  <td class="color-cell">
                    <div class="phase-color-indicator" [style.background-color]="phase.color || 'rgb(128, 128, 128)'"></div>
                  </td>
                  <td class="label-cell">{{ phase.label }}</td>
                  <td class="fraction-cell">{{ phase.fraction | number : '1.3-3' }}</td>
                  <td class="porosity-cell">{{ phase.porosity != null ? (phase.porosity | number : '1.3-3') : '-' }}</td>
                  <td class="permeability-cell">
                    {{ isFieldDisabled(phase) ? '-' : (phase.permeabilityValue != null ? (phase.permeabilityValue | number : '1.2-2') : '-') }}
                  </td>
                  <td
                    class="selectable-cell"
                    [class.disabled]="isFieldDisabled(phase) || isReadonly"
                    [class.readonly]="isReadonly"
                    [class.selected]="!!phase.capillaryPressure"
                    (click)="selectCapillaryPressure(phase)"
                  >
                    @if (isFieldDisabled(phase)) {
                      <span>-</span>
                    } @else if (phase.capillaryPressure) {
                      <span class="selected-name">{{ phase.capillaryPressure.simulationName }}</span>
                    } @else {
                      <span class="select-placeholder">Select PC<br/>Simulation</span>
                    }
                  </td>
                  <td
                    class="selectable-cell"
                    [class.disabled]="isFieldDisabled(phase) || isReadonly"
                    [class.readonly]="isReadonly"
                    [class.selected]="!!phase.absolutePermeability"
                    (click)="selectAbsolutePermeability(phase)"
                  >
                    @if (isFieldDisabled(phase)) {
                      <span>-</span>
                    } @else if (phase.absolutePermeability) {
                      <span class="selected-name">{{ phase.absolutePermeability.simulationName }}</span>
                    } @else {
                      <span class="select-placeholder">Select<br/>Permeability</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="actions-row">
        @if (isReadonly) {
          <button mat-raised-button color="primary" (click)="back()">
            Back
          </button>
        } @else {
          <button mat-button (click)="cancel()">
            Cancel
          </button>
          <button
            mat-raised-button
            color="primary"
            [disabled]="!canSave()"
            (click)="save()"
          >
            Save
          </button>
        }
      </div>
    </div>
  </div>
</mat-card>
}
